<div class="memory-scene">
  <!-- FREE RECALL -->
  <ng-container *ngIf="phase === 'free'">
    <div style="margin-bottom:16px">
      <div class="cog-label" style="color:var(--sharp-accent);margin-bottom:10px">{{ 'cognitive.sharpV2.memoryScene.label' | translate }}</div>
      <h2 class="cog-h2">{{ 'cognitive.sharpV2.memoryScene.titleFree' | translate }}</h2>
      <p class="cog-subtitle">{{ 'cognitive.sharpV2.memoryScene.subtitleFree' | translate }}</p>
    </div>

    <div id="scene-questions">
      <div class="cog-card">
        <div style="display:flex;flex-direction:column;gap:14px">
          <div>
            <label style="font-size:12px;color:var(--sharp-text-muted);display:block;margin-bottom:4px">{{ 'cognitive.sharpV2.memoryScene.qName' | translate }}</label>
            <input
              #nameEl
              type="text"
              class="cog-input"
              id="fr-name"
              [(ngModel)]="freeName"
              autocomplete="off"
              autocapitalize="words"
              [placeholder]="'cognitive.sharpV2.memoryScene.namePlaceholder' | translate"
            />
          </div>
          <div>
            <label style="font-size:12px;color:var(--sharp-text-muted);display:block;margin-bottom:4px">{{ 'cognitive.sharpV2.memoryScene.qColor' | translate }}</label>
            <div style="display:flex;flex-wrap:wrap;gap:8px" id="color-pick">
              <div
                *ngFor="let c of colorEntries"
                class="color-circle"
                [style.background]="c.hex"
                [class.selected]="freeColor === c.name"
                [attr.data-color]="c.name"
                [attr.aria-label]="colorLabelKey(c.name) | translate"
                role="button"
                tabindex="0"
                (click)="pickColor(c.name)"
              ></div>
            </div>
          </div>
          <div>
            <label style="font-size:12px;color:var(--sharp-text-muted);display:block;margin-bottom:4px">{{ 'cognitive.sharpV2.memoryScene.qObject' | translate }}</label>
            <input
              type="text"
              class="cog-input"
              id="fr-object"
              [(ngModel)]="freeObject"
              autocomplete="off"
              autocapitalize="none"
              [placeholder]="'cognitive.sharpV2.memoryScene.objectPlaceholder' | translate"
            />
          </div>
          <div>
            <label style="font-size:12px;color:var(--sharp-text-muted);display:block;margin-bottom:4px">{{ 'cognitive.sharpV2.memoryScene.qPlace' | translate }}</label>
            <div style="display:flex;flex-wrap:wrap;gap:6px" id="place-pick">
              <button
                *ngFor="let p of placeOptions"
                type="button"
                class="place-btn"
                [class.selected]="freePlace === p"
                [attr.data-place]="p"
                (click)="pickPlace(p)"
              >{{ placeLabelKey(p) | translate }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="cog-spacer"></div>
    <button type="button" class="cog-btn cog-btn-primary" (click)="submitFree()">{{ 'cognitive.sharpV2.memoryScene.submit' | translate }}</button>
    <button type="button" class="cog-btn cog-btn-ghost" style="margin-top:4px" (click)="submitFree(true)">{{ 'cognitive.sharpV2.memoryScene.dontRemember' | translate }}</button>
  </ng-container>

  <!-- CUED (one screen per field) -->
  <ng-container *ngIf="phase === 'cued'">
    <div class="cog-spacer"></div>
    <div class="cog-text-center cog-mb-16">
      <div class="cog-label" style="color:var(--sharp-accent);margin-bottom:8px">{{ 'cognitive.sharpV2.memoryScene.titleCued' | translate }}</div>
      <p class="cog-subtitle">{{ 'cognitive.sharpV2.memoryScene.subtitleCued' | translate }}</p>
    </div>

    <div class="cog-card" *ngIf="currentCued as q">
      <label style="font-size:15px;color:var(--sharp-navy);font-weight:600;display:block;margin-bottom:14px;text-align:center">{{ ('cognitive.sharpV2.memoryScene.cuedPrompt.' + q.key) | translate }}</label>
      <ng-container *ngIf="q.key === 'color'; else nonColorCued">
        <div style="display:flex;gap:12px;justify-content:center">
          <div
            *ngFor="let opt of q.options"
            class="color-circle"
            style="width:48px;height:48px"
            [style.background]="getColorHex(opt)"
            [class.selected]="cuedAnswers[q.key] === opt"
            [attr.data-cued]="q.key"
            [attr.data-val]="opt"
            [attr.aria-label]="colorLabelKey(opt) | translate"
            role="button"
            tabindex="0"
            (click)="pickCued(q.key, opt)"
          ></div>
        </div>
      </ng-container>
      <ng-template #nonColorCued>
        <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap">
          <button
            *ngFor="let opt of q.options"
            type="button"
            class="place-btn"
            [class.selected]="cuedAnswers[q.key] === opt"
            [attr.data-cued]="q.key"
            [attr.data-val]="opt"
            (click)="pickCued(q.key, opt)"
          >{{ ('cognitive.sharpV2.recall.' + poolKeyFor(q.key) + '.' + opt.toLowerCase()) | translate }}</button>
        </div>
      </ng-template>
    </div>

    <div class="cog-spacer"></div>
  </ng-container>

  <!-- SUMMARY before continuing (correction brief 21.5). Her answers only, with
       no correct/incorrect marking: this is a scored recall, and a tick here
       would read as feedback in the middle of the battery. -->
  <ng-container *ngIf="phase === 'review'">
    <div class="cog-spacer"></div>
    <div class="cog-text-center cog-mb-16">
      <h2 class="cog-h2">{{ 'cognitive.sharpV2.memoryScene.reviewTitle' | translate }}</h2>
      <p class="cog-subtitle">{{ 'cognitive.sharpV2.memoryScene.reviewSubtitle' | translate }}</p>
    </div>

    <div class="cog-card">
      <div class="ms-review">
        <div class="ms-review-row" *ngFor="let r of summaryRows">
          <span class="ms-review-label">{{ ('cognitive.sharpV2.memoryScene.q' + (r.key | titlecase)) | translate }}</span>
          <span class="ms-review-value" *ngIf="r.value">
            <span class="ms-review-dot" *ngIf="r.color" [style.background]="r.color"></span>{{ r.value }}
          </span>
          <span class="ms-review-value ms-review-blank" *ngIf="!r.value">
            {{ 'cognitive.sharpV2.memoryScene.reviewBlank' | translate }}
          </span>
        </div>
      </div>
    </div>

    <div class="cog-spacer"></div>
    <button type="button" class="cog-btn cog-btn-primary" (click)="confirmReview()">
      {{ 'cognitive.sharpV2.memoryScene.reviewContinue' | translate }}
    </button>
  </ng-container>
</div>
